import { FC } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Modal, Tooltip } from "@mui/material";
import {
	InfoIcon,
	TruckFlyoutAccountDetails,
	TruckFlyoutContainer,
	TruckFlyoutDeliveryDetails,
	TruckFlyoutDetails,
	TruckFlyoutHeaderContainer,
	TruckFlyoutHeaderDetailsContainer,
	TruckFlyoutHeaderDivider,
	TruckFlyoutIdHeader,
	TruckFlyoutOrderDate,
	TruckFlyoutOrdersContainer,
	TruckFlyoutRequestedDeliveryDate,
	TruckFlyoutResultsText,
	TruckFlyoutStatusChip,
	TruckFlyoutStatusDetails,
	TruckFlyoutStopAddress,
	TruckFlyoutStopContainer,
	TruckFlyoutStopDividerContainer,
	TruckFlyoutTitleContainer
} from "./TruckDetailsFlyout.styles";
import { models } from "types/api/viewModels";
import { dateFormatTwoDigitDays } from "utils/date";
import { convertToTitleCase } from "utils/string";
import TruckDetailsFlyoutOrderCard from "../components/TruckDetailsFlyoutOrderCard/TruckDetailsFlyoutOrderCard";
import ExportButtons from "components/ExportFile/ExportButtons";
import {
	AccountNumberText,
	CabinetCountText,
	EstimatedDeliveryText,
	ExportDetails,
	OrderDateText,
	OrderText,
	ProductLineText,
	ReplacmentConfirmationOrderPending,
	StopNumberText,
	TruckDetailsFlyoutEstimatedDeliveryTooltip,
	TruckDetailsFlyoutRequestedDeliveryLabel,
	TruckDetailsFlyoutTitle,
	TruckNameText,
	TruckPendingText,
	UnavailableDataPlaceholderText
} from "constants/text";
import TruckDetailsFlyoutOrderCardPendingStatusMessage from "../components/TruckDetailsFlyoutOrderCardPendingStatusMessage/TruckDetailsFlyoutOrderCardPendingStatusMessage";
import { generateTruckDetailsExcel } from "../helpers/TruckDetailsExportHelper";
import TruckCubeCountTracker from "../TruckCubeCountTracker/TruckCubeCountTracker";

interface TruckDetailsFlyoutProps {
	isOpen: boolean;
	onClose: () => void;
	truck: models["TruckViewModel"] | null;
}

const TruckDetailsFlyout: FC<TruckDetailsFlyoutProps> = ({ isOpen, onClose, truck }) => {
	const truckStatus = truck
		? ((truck.truckStatus ?? "").toLowerCase().replace("_", " ") as "in production" | "complete")
		: "unknown";
	const eta = truck?.stops?.[0]?.orders?.[0]?.eta?.slice(0, 5) ?? ReplacmentConfirmationOrderPending;
	const isPending = !truck?.stops?.some((stop) => stop.orders && stop.orders.length > 0);
	return (
		<Modal
			open={isOpen}
			onClose={onClose}
		>
			<TruckFlyoutContainer
				elevation={3}
				square
				data-testid="truck-flyout"
			>
				<TruckFlyoutHeaderContainer>
					<TruckFlyoutTitleContainer>
						<h5>{TruckDetailsFlyoutTitle}</h5>
						<IconButton
							onClick={onClose}
							data-testid="truckDetails-closeButton"
						>
							<CloseIcon />
						</IconButton>
					</TruckFlyoutTitleContainer>
					{!isPending && truck && (
						<TruckFlyoutHeaderDetailsContainer>
							<TruckFlyoutIdHeader>
								<h6 data-testid="truckDetails-truckNumber">
									# {truck.truckNumber ?? TruckPendingText}
								</h6>
								<ExportButtons
									buttonHeader={ExportDetails}
									generateExcel={() => generateTruckDetailsExcel(truck)}
									isOnlyExcel
									largePDF={false}
								/>
							</TruckFlyoutIdHeader>
							<TruckCubeCountTracker truck={truck} />
						</TruckFlyoutHeaderDetailsContainer>
					)}
				</TruckFlyoutHeaderContainer>
				<TruckFlyoutStatusDetails>
					<TruckFlyoutOrderDate data-testid="truckDetails-orderDate">
						{OrderDateText}:{" "}
						{truck?.submittedOn
							? dateFormatTwoDigitDays(truck.submittedOn)
							: UnavailableDataPlaceholderText}
					</TruckFlyoutOrderDate>
					{!isPending && (
						<TruckFlyoutStatusChip
							label={convertToTitleCase(truckStatus)}
							data-testid="truckDetails-truckStatus"
						/>
					)}
					<TruckFlyoutDeliveryDetails>
						<div>
							<span>{EstimatedDeliveryText}</span>
							<Tooltip
								title={TruckDetailsFlyoutEstimatedDeliveryTooltip}
								placement="top-end"
								slotProps={{
									popper: {
										modifiers: [
											{
												name: "offset",
												options: {
													offset: [100, -14]
												}
											}
										]
									}
								}}
							>
								<InfoIcon />
							</Tooltip>
						</div>
						<h5 data-testid="truckDetails-estimatedDeliveryDate">{eta}</h5>
					</TruckFlyoutDeliveryDetails>
					<TruckFlyoutRequestedDeliveryDate data-testid="truckDetails-requestedDeliveryDate">
						{TruckDetailsFlyoutRequestedDeliveryLabel}:{" "}
						{truck?.requestedDeliveryDate
							? dateFormatTwoDigitDays(truck.requestedDeliveryDate)
							: UnavailableDataPlaceholderText}
					</TruckFlyoutRequestedDeliveryDate>
				</TruckFlyoutStatusDetails>
				<TruckFlyoutDetails>
					<TruckFlyoutAccountDetails>
						<div>
							<span>{AccountNumberText}</span>
							<span data-testid="truckDetails-accountNumber">
								{truck?.accountNumber || truck?.accountName
									? [truck?.accountNumber, truck?.accountName].filter(Boolean).join(" - ")
									: UnavailableDataPlaceholderText}
							</span>
						</div>
						<div data-testid="truckDetails-truckName">
							<span>{TruckNameText}</span>
							<span>{truck?.truckName ?? UnavailableDataPlaceholderText}</span>
						</div>
						<div data-testid="truckDetails-productLine">
							<span>{ProductLineText}</span>
							<span>{truck?.productLine?.description ?? UnavailableDataPlaceholderText}</span>
						</div>
						<div>
							<span data-testid="truckDetails-cabCount">{CabinetCountText}</span>
							<span>{truck?.cabinetCount ?? UnavailableDataPlaceholderText}</span>
						</div>
					</TruckFlyoutAccountDetails>
					<TruckFlyoutHeaderDivider />
					<TruckFlyoutOrdersContainer>
						{isPending ? (
							<TruckDetailsFlyoutOrderCardPendingStatusMessage />
						) : (
							<>
								{truck?.stops?.map((stop, index) => (
									<TruckFlyoutStopContainer
										data-testid="truckDetails-stopCard"
										key={stop.truckStopId}
									>
										<TruckFlyoutStopAddress>
											<span>
												{StopNumberText}
												{index + 1}
											</span>
											<span>{`${stop.address?.name}, ${stop.address?.line1}, ${stop.address?.city}, ${stop.address?.state} ${stop.address?.zip}`}</span>
										</TruckFlyoutStopAddress>
										<TruckFlyoutResultsText data-testid="truckDetails-totalOrders">
											{stop.orders?.length} {OrderText}
											{stop.orders?.length !== 1 ? "S" : ""}
										</TruckFlyoutResultsText>
										{stop?.orders?.map((order) => (
											<TruckDetailsFlyoutOrderCard
												key={order.orderId}
												order={order}
											/>
										))}
										{truck.stops && index !== truck.stops.length - 1 && (
											<TruckFlyoutStopDividerContainer>
												<TruckFlyoutHeaderDivider />
											</TruckFlyoutStopDividerContainer>
										)}
									</TruckFlyoutStopContainer>
								))}
							</>
						)}
					</TruckFlyoutOrdersContainer>
				</TruckFlyoutDetails>
			</TruckFlyoutContainer>
		</Modal>
	);
};

export default TruckDetailsFlyout;
